import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import Select from '../../../components/Select';
import Strings from '../../../Strings';
import { actions } from '../redux/actions';
import './Configuration.scss';
import {
  GET_ORG_ROLES_DETAIL_RESULT,
  UPDATE_ORG_ROLES_DETAIL_ERROR,
  UPDATE_ORG_TEMPLATE_ROLES_ERROR,
} from '../redux/constants';
import { notificationActions } from '../../../components/Notification/redux/actions';
import JsonSchemaForm from '../../../containers/Forms/JsonSchemaForm';
import { validator } from '../../../containers/Forms/JsonSchemaFormHelpers';
import { simpleHash } from '../../../containers/Forms/JsonSchemaFormHelpers';

function OrgRolePermissions(props) {
  const {
    roleId,
    roles,
    templateRoles,
    isLoading,
    getOrgRole,
    getOrgRoleConfig,
    getOrgTemplateRoles,
    updateOrgTemplateRoles,
    updateOrgRoleConfig,
    organizationId,
    showNotification,
    isTemplate,
    readOnly,
    onExternalSubmit, // New prop for external submit callback
  } = props;

  const [role, setRole] = React.useState(roleId);
  const [data, setData] = React.useState();
  const [template, setTemplate] = React.useState();
  const [templateRolesData, setTemplateRolesData] = React.useState();
  const [key, setKey] = React.useState((new Date()).getDate());
  const [activeModule, setActiveModule] = React.useState('');
  const [moduleErrors, setModuleErrors] = React.useState({});
  const [isFormValid, setIsFormValid] = React.useState(false);

  const formRef = React.useRef();
  
  // External submit handler that can be called from parent component
  const handleExternalSubmit = React.useCallback(() => {
    if (formRef.current && isFormValid) {
      formRef.current.submit();
    }
  }, [isFormValid]);
  
  // Expose the submit handler to parent component
  React.useEffect(() => {
    if (onExternalSubmit) {
      onExternalSubmit(handleExternalSubmit);
    }
  }, [onExternalSubmit, handleExternalSubmit]);
  
  // Also call it when the form is ready (when we have template and data)
  React.useEffect(() => {
    if (onExternalSubmit && template && data) {
      onExternalSubmit(handleExternalSubmit);
    }
  }, [onExternalSubmit, handleExternalSubmit, template, data]);
  
  const request = React.useRef({
    organizationId: decodeURIComponent(organizationId),
    roleUuid: role,
  });

  React.useEffect(() => {
    if (isTemplate) {
      getOrgTemplateRoles({ organizationId: decodeURIComponent(organizationId) });
      return;
    }
    getOrgRole(request.current);
  }, [getOrgRole, getOrgTemplateRoles, isTemplate, organizationId]);

  const getData = () => {
    if (!role || isTemplate) {
      return;
    }
    request.current.roleUuid = role;
    getOrgRoleConfig(request.current).then(resp => {
      if (resp?.type === GET_ORG_ROLES_DETAIL_RESULT) {
        const permissions = cloneDeep(resp.response?.role_permissions || {});
        Object.keys(permissions).forEach(module => {
          if (!resp.response?.org_permissions_schema?.$defs?.PermissionConfig?.properties?.[module]) {
            delete permissions[module];
          }
        });
        setData(permissions);
        setTemplate({
          ...resp.response?.org_permissions_schema,
          ['$id']: `${resp.response?.org_permissions_schema['$id']}_${simpleHash(JSON.stringify(resp.response?.org_permissions_schema))}`,
        });
        setKey(prevKey => prevKey + 1);
      }
    });
  };

  React.useEffect(() => {
    if (!role || isTemplate) {
      return;
    }
    getData();
  }, [role, isTemplate]);

  React.useEffect(() => {
    if (!isTemplate) {
      return;
    }
    setTemplateRolesData(templateRoles);
    const rolesMap = templateRoles?.roles || {};
    const roleKeys = Object.keys(rolesMap);
    if (!role && roleKeys.length) {
      setRole(roleKeys[0]);
      return;
    }
    if (role) {
      const roleEntry = rolesMap[role];
      const roleData = roleEntry?.permissions || roleEntry?.role_permissions || roleEntry;
      setData(cloneDeep(roleData));
      setActiveModule('');
    } else {
      setData(undefined);
    }
    if (templateRoles?.template_permissions_schema) {
      const schema = templateRoles.template_permissions_schema;
      setTemplate({
        ...schema,
        ['$id']: `${schema['$id']}_${simpleHash(JSON.stringify(schema))}`,
      });
    }
    setKey(prevKey => prevKey + 1);
  }, [isTemplate, role, templateRoles]);

  const resolveSchemaRef = React.useCallback((schema, ref) => {
    if (!schema || !ref || !ref.startsWith('#/')) {
      return undefined;
    }
    return ref
      .slice(2)
      .split('/')
      .reduce((acc, keyPart) => acc?.[keyPart], schema);
  }, []);

  const permissionsSchema = React.useMemo(() => {
    if (!template) {
      return undefined;
    }
    if (template?.$defs?.PermissionConfig?.properties) {
      return template.$defs.PermissionConfig;
    }
    const directSchema = template?.properties?.permissions;
    if (directSchema?.properties) {
      return directSchema;
    }
    const refSchema = resolveSchemaRef(template, directSchema?.$ref);
    if (refSchema?.properties) {
      return refSchema;
    }
    if (template?.properties) {
      return template;
    }
    return undefined;
  }, [template, resolveSchemaRef]);

  const permissionData = React.useMemo(() => {
    if (data?.permissions && typeof data.permissions === 'object' && !Array.isArray(data.permissions)) {
      return data.permissions;
    }
    return data;
  }, [data]);

  const moduleKeys = React.useMemo(() => {
    const schemaModules = Object.keys(permissionsSchema?.properties || {});
    const dataModules = Object.keys(permissionData || {});
    return schemaModules.concat(dataModules.filter(module => !schemaModules.includes(module)));
  }, [permissionsSchema, permissionData]);

  React.useEffect(() => {
    if ((!activeModule || !moduleKeys.includes(activeModule)) && moduleKeys.length) {
      setActiveModule(moduleKeys[0]);
    }
  }, [activeModule, moduleKeys]);

  const getModuleLabel = moduleKey => {
    const moduleSchema = permissionsSchema?.properties?.[moduleKey];
    return moduleSchema?.description || moduleSchema?.title || moduleKey;
  };

  const getModuleErrors = React.useCallback((errors = []) => {
    const nextErrors = {};
    errors.forEach(error => {
      const parts = error?.property?.split('.').filter(Boolean) || [];
      const scopeIndex = parts.findIndex(part => part === 'permissions');
      if (scopeIndex !== -1 && parts.length > scopeIndex + 1) {
        const moduleKey = parts[scopeIndex + 1];
        nextErrors[moduleKey] = true;
        return;
      }
      if (parts.length > 0 && moduleKeys.includes(parts[0])) {
        nextErrors[parts[0]] = true;
      }
    });
    return nextErrors;
  }, [moduleKeys]);

  const buildModuleUiSchema = (moduleKey, modules) => {
    const moduleUiSchema = {
      'ui:order': [moduleKey],
      ...(modules || []).reduce((acc, keyItem) => {
        if (keyItem !== moduleKey) {
          acc[keyItem] = { 'ui:widget': 'hidden' };
        }
        return acc;
      }, {}),
      [moduleKey]: { 'ui:options': { showWhenDisabled: true }, 'ui:moduleLevel': true },
    };

    if (template?.properties?.permissions) {
      return {
        ...(Object.keys(template?.properties || {}).reduce((acc, propKey) => {
          if (propKey !== 'permissions') {
            acc[propKey] = { 'ui:widget': 'hidden' };
          }
          return acc;
        }, {})),
        permissions: moduleUiSchema,
      };
    }

    return {
      name: { 'ui:widget': 'hidden' },
      masked_id: { 'ui:widget': 'hidden' },
      ...moduleUiSchema,
    };
  };

  const baseUiSchema = React.useMemo(() => ({
    ...(template?.properties?.permissions ? { 'ui:order': ['permissions'] } : {}),
    name: { 'ui:widget': 'hidden' },
    masked_id: { 'ui:widget': 'hidden' },
  }), [template]);

  const isPlainObject = value =>
    value && typeof value === 'object' && !Array.isArray(value);

  const resolveSchema = React.useCallback(schema => {
    if (!schema) {
      return undefined;
    }
    return schema.$ref ? resolveSchemaRef(template, schema.$ref) : schema;
  }, [resolveSchemaRef, template]);

  const fillRequiredBooleans = React.useCallback((currentValue, schema) => {
    const resolvedSchema = resolveSchema(schema);
    if (!resolvedSchema) {
      return { value: currentValue, changed: false };
    }

    if (resolvedSchema.type === 'boolean') {
      if (currentValue === undefined) {
        return { value: false, changed: true };
      }
      return { value: currentValue, changed: false };
    }

    if (resolvedSchema.type === 'object' || resolvedSchema.properties) {
      const base = isPlainObject(currentValue) ? { ...currentValue } : {};
      let changed = false;
      const requiredKeys = resolvedSchema.required || [];
      requiredKeys.forEach(key => {
        const propSchema = resolvedSchema.properties?.[key];
        const { value, changed: childChanged } = fillRequiredBooleans(base[key], propSchema);
        if (childChanged) {
          base[key] = value;
          changed = true;
        }
      });
      return { value: base, changed };
    }

    return { value: currentValue, changed: false };
  }, [resolveSchema, isPlainObject]);

  const fillModulesFromSchema = React.useCallback((currentValue, schema) => {
    const resolvedSchema = resolveSchema(schema);
    if (!resolvedSchema?.properties) {
      return { value: currentValue, changed: false };
    }
    const base = isPlainObject(currentValue) ? { ...currentValue } : {};
    let changed = false;
    Object.keys(resolvedSchema.properties).forEach(moduleKey => {
      const moduleSchema = resolvedSchema.properties[moduleKey];
      const { value, changed: moduleChanged } = fillRequiredBooleans(base[moduleKey], moduleSchema);
      if (moduleChanged) {
        base[moduleKey] = value;
        changed = true;
      }
    });
    return { value: base, changed };
  }, [fillRequiredBooleans, resolveSchema, isPlainObject]);

  const ensureSchemaModules = React.useCallback(currentData => {
    if (!currentData) {
      return { data: currentData, changed: false };
    }

    if (!permissionsSchema?.properties) {
      return { data: currentData, changed: false };
    }

    let changed = false;
    const nextData = { ...currentData };
    const permissionsContainer =
      typeof nextData.permissions === 'object' && !Array.isArray(nextData.permissions)
        ? 'permissions'
        : template?.properties?.permissions
          ? 'permissions'
          : null;
    const target = permissionsContainer ? { ...(nextData.permissions || {}) } : { ...nextData };

    const { value: nextTarget, changed: targetChanged } = fillModulesFromSchema(target, permissionsSchema);
    if (targetChanged) {
      changed = true;
    }

    if (changed) {
      if (permissionsContainer) {
        nextData.permissions = nextTarget;
      } else {
        Object.assign(nextData, nextTarget);
      }
    }

    return { data: nextData, changed };
  }, [permissionsSchema, template, fillModulesFromSchema]);

  React.useEffect(() => {
    if (!data || !template) {
      return;
    }
    const { data: normalized, changed } = ensureSchemaModules(data);
    if (changed) {
      setData(normalized);
    }
  }, [data, template, ensureSchemaModules]);

  React.useEffect(() => {
    if (!data || !template) {
      return;
    }
    const { errors } = validator.validateFormData(data, template);
    setIsFormValid(errors.length === 0);
    setModuleErrors(getModuleErrors(errors));
  }, [data, template, getModuleErrors]);

  const onSave = e => {
    if (isTemplate) {
      const rolesMap = templateRolesData?.roles || {};
      const updatedRoles = {
        ...rolesMap,
        [role]: {
          ...rolesMap[role],
          permissions: e.formData,
        },
      };
      const payload = { roles: updatedRoles };
      updateOrgTemplateRoles(payload, { organizationId: request.current.organizationId }).then(resp => {
        if (resp?.type === UPDATE_ORG_TEMPLATE_ROLES_ERROR) {
          showNotification(resp.response?.data?.error?.message, 5000, true);
        } else {
          setTemplateRolesData(prev => ({ ...prev, roles: updatedRoles }));
          showNotification(Strings.userPermissionsSaved);
        }
      });
      return;
    }
    updateOrgRoleConfig(e.formData, request.current).then(resp => {
      if (resp?.type === UPDATE_ORG_ROLES_DETAIL_ERROR) {
        showNotification(resp.response?.data?.error?.message, 5000, true);
      } else {
        showNotification(Strings.userPermissionsSaved);
        getData();
      }
    });
  };

  const roleOptions = React.useMemo(() => {
    if (isTemplate) {
      const rolesMap = templateRolesData?.roles || {};
      return Object.keys(rolesMap).map(roleName => ({
        value: roleName,
        label: roleName,
      }));
    }
    return Object.keys(roles || {}).map(r => {
      return { value: roles[r].role_uuid, label: `${roles[r].archetype} (${roles[r].title})` };
    });
  }, [isTemplate, roles, templateRolesData]);

  const handleRoleChange = option => {
    const nextRole = option?.value || '';
    setRole(nextRole);
    if (isTemplate) {
      const rolesMap = templateRolesData?.roles || {};
      const roleEntry = rolesMap[nextRole];
      const roleData = roleEntry?.permissions || roleEntry?.role_permissions || roleEntry;
      setData(nextRole ? cloneDeep(roleData) : undefined);
      setActiveModule('');
      setKey(prevKey => prevKey + 1);
    }
  };

  return (
    isLoading ? <div className="spinner" /> : (
      <>
        {roleOptions.length > 0 && !roleId && (
          <div className="dd-ele">
            <div className="dd-title">{Strings.selectRole}</div>
            <Select
              id="org-role"
              value={role}
              autoWidth
              onChange={handleRoleChange}
              data={roleOptions}
            />
          </div>
        )}
        {role && (
          <div className="config-sections scrollbar-right-margin">
            {template && data && (
              moduleKeys.length ? (
                <div className="config-modules">
                  <OverlayScrollbarsComponent
                    defer
                    options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
                    className="scrollbar-right-margin-min config-modules-nav"
                  >
                    <div className="config-modules-label">{Strings.modules}</div>
                    <div className="tab-buttons-row config-modules-tabs">
                      {moduleKeys.map(moduleKey => (
                        <button
                          key={moduleKey}
                          type="button"
                          className={`tabButton tab-label${activeModule === moduleKey ? ' active-tab-label' : ''}`}
                          onClick={() => setActiveModule(moduleKey)}
                        >
                          {getModuleLabel(moduleKey)}
                          {moduleErrors[moduleKey] && <span className="input-error module-error-icon" />}
                        </button>
                      ))}
                    </div>
                  </OverlayScrollbarsComponent>
                  <div className="config-modules-content">
                    {activeModule && (
                      <JsonSchemaForm
                        schema={template}
                        data={data}
                        onSubmit={onSave}
                        onChange={e => {
                          setData(e.formData);
                          setIsFormValid(e.errors?.length === 0);
                          setModuleErrors(getModuleErrors(e.errors));
                        }}
                        uiSchema={buildModuleUiSchema(activeModule, moduleKeys)}
                        id={`${role}_${key}_${activeModule}`}
                        key={`${role}_${key}_${activeModule}`}
                        submitDisabled={!isFormValid}
                        readOnly={readOnly}
                        hideSubmitButton={!!roleId}
                        formRef={formRef}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <JsonSchemaForm
                  schema={template}
                  data={data}
                  onSubmit={onSave}
                  onChange={e => {
                    setData(e.formData);
                    setIsFormValid(e.errors?.length === 0);
                    setModuleErrors(getModuleErrors(e.errors));
                  }}
                  uiSchema={baseUiSchema}
                  id={`${role}_${key}`}
                  key={`${role}_${key}`}
                  submitDisabled={!isFormValid}
                  readOnly={readOnly}
                  hideSubmitButton={!!roleId}
                  formRef={formRef}
                />
              )
            )}
          </div>
        )}
      </>
    )
  );
}

OrgRolePermissions.propTypes = {
  getOrgConfig: PropTypes.func,
  getOrgRole: PropTypes.func,
  getOrgRoleConfig: PropTypes.func,
  getOrgTemplateRoles: PropTypes.func,
  updateOrgTemplateRoles: PropTypes.func,
  isLoading: PropTypes.any,
  orgRoleConfig: PropTypes.any,
  organizationId: PropTypes.any,
  isTemplate: PropTypes.bool,
  roles: PropTypes.any,
  templateRoles: PropTypes.any,
  updateOrgRoleConfig: PropTypes.func,
  showNotification: PropTypes.func,
  onExternalSubmit: PropTypes.func, // New prop
};

const mapStateToProps = state => {
  const { organizations } = state.superUser;
  return {
    roles: organizations?.roles,
    orgRoleConfig: organizations?.orgRoleConfig,
    isLoading: organizations?.isLoading,
    templateRoles: organizations?.templateRoles,
  };
};

const mapDispatchToProps = dispatch => ({
  getOrgRole: pageRequest => dispatch(actions.getOrgRole(pageRequest)),
  getOrgRoleConfig: pageRequest => dispatch(actions.getOrgRoleConfig(pageRequest)),
  getOrgTemplateRoles: pageRequest => dispatch(actions.getOrgTemplateRoles(pageRequest)),
  updateOrgTemplateRoles: (data, pageRequest) => dispatch(actions.updateOrgTemplateRoles(data, pageRequest)),
  updateOrgRoleConfig: (data, pageRequest) => dispatch(actions.updateOrgRoleConfig(data, pageRequest)),
  getTemplate: pageRequest => dispatch(actions.getTemplateOrgConfig(pageRequest)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrgRolePermissions);
